<template>
  <transition name="banner">
    <div class="site-banner"
         v-if="banner.enabled && banner.text && !dismissed"
         :style="{ backgroundColor: banner.color }">
      <p>
        <a v-if="safeUrl" :href="safeUrl" target="_blank" rel="noopener noreferrer">{{ banner.text }}</a>
        <template v-else>{{ banner.text }}</template>
      </p>
      <img class="close" src="../../assets/icons/cross.svg" alt="Fermer" @click="dismiss()"/>
    </div>
  </transition>
</template>

<script setup lang="ts">
import {computed, onMounted, reactive, ref} from "vue";
import {HTTPAxios} from "../../object/HTTPAxios";
import {Banner, defaultBanner} from "../../object/Banner";

const banner = reactive<Banner>(defaultBanner());
const dismissed = ref(false);

// Only turn the banner into a link for http(s) or root-relative URLs; anything
// else (e.g. a "javascript:" scheme) is ignored and the text stays plain.
const safeUrl = computed(() => {
  const url = (banner.url ?? '').trim();
  return /^(https?:\/\/|\/)/i.test(url) ? url : '';
});

onMounted(() => {
  new HTTPAxios('banner', null, false).get().then((response) => {
    if (response.data) {
      banner.text = response.data.text ?? '';
      banner.enabled = response.data.enabled ?? false;
      banner.color = response.data.color || banner.color;
      banner.url = response.data.url ?? '';
    }
  }).catch(() => {
    // No banner configured yet (or backend unreachable): silently show nothing.
  });
});

function dismiss() {
  dismissed.value = true;
}
</script>

<style scoped lang="scss">
.site-banner {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 8px 44px;
  position: relative;
  color: white;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);

  p {
    margin: 0;
    font-family: Jost-Medium, serif;
    font-size: 15px;
    text-align: center;
    line-height: 1.35;

    a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
      cursor: pointer;

      &:hover {
        opacity: 0.85;
      }
    }
  }

  .close {
    position: absolute;
    right: 16px;
    width: 14px;
    cursor: pointer;
    filter: brightness(0) invert(1);
    opacity: 0.85;

    &:hover {
      opacity: 1;
    }
  }
}

.banner-enter-active,
.banner-leave-active {
  transition: all 0.25s ease;
}

.banner-enter-from,
.banner-leave-to {
  opacity: 0;
  transform: translateY(-100%);
}
</style>
